/**
 * Goals Create Page — structured form with title, category, dates, description.
 *
 * Calls POST /v1/hros/goals via BLL (proxy → Directus goals table).
 */

import * as React from 'react';
import { useNavigate } from 'react-router';
import {
  Button,
  Card,
  CardContent,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@yesbos/ui';
import { ArrowLeft } from 'lucide-react';

const GATEWAY_URL = import.meta.env.VITE_GATEWAY_URL ?? 'http://localhost:9200';

// ── Categories ────────────────────────────────────────────────────────────────

const CATEGORIES = [
  'Performance',
  'Learning & Development',
  'Leadership',
  'Customer Success',
  'Innovation',
  'Compliance',
  'Other',
];

// ── BLL helper ────────────────────────────────────────────────────────────────

async function callBLL(path: string, body: unknown) {
  const token = localStorage.getItem('axon_token') ?? '';
  const res = await fetch(`${GATEWAY_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  return res.json();
}

// ── Component ────────────────────────────────────────────────────────────────

export function GoalsCreatePage() {
  const navigate = useNavigate();

  const [title, setTitle] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [category, setCategory] = React.useState('');
  const [dueDate, setDueDate] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (!title.trim()) { setError('Title is required.'); return; }

    setSubmitting(true);
    try {
      const res = await callBLL('/v1/hros/goals', {
        title: title.trim(),
        description: description.trim() || undefined,
        category: category || undefined,
        due_date: dueDate || undefined,
        status: 'draft',
        progress: 0,
      });
      if (res.success) {
        navigate(`/goals/${res.data.id}`);
      } else {
        setError(res.error?.message ?? 'Failed to create goal.');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="flex flex-col gap-6 p-6 max-w-2xl">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/goals')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-2xl font-semibold">Add Goal</h1>
      </div>

      <form onSubmit={handleSubmit}>
        <Card>
          <CardContent className="pt-5 space-y-5">
            {/* Title */}
            <div>
              <label className="text-sm font-medium block mb-1.5">
                Goal Title <span className="text-destructive">*</span>
              </label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Complete AWS certification by Q3"
                autoFocus
              />
            </div>

            {/* Description */}
            <div>
              <label className="text-sm font-medium block mb-1.5">
                Description <span className="text-muted-foreground text-xs">(optional)</span>
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe what success looks like…"
                rows={3}
                className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              />
            </div>

            {/* Category + Due Date */}
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="text-sm font-medium block mb-1.5">
                  Category <span className="text-muted-foreground text-xs">(optional)</span>
                </label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select category…" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium block mb-1.5">
                  Due Date <span className="text-muted-foreground text-xs">(optional)</span>
                </label>
                <Input
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  min={today}
                />
              </div>
            </div>

            {/* Error */}
            {error && <p className="text-sm text-destructive">{error}</p>}

            {/* Actions */}
            <div className="flex gap-3 pt-1">
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Creating…' : 'Create Goal'}
              </Button>
              <Button type="button" variant="outline" onClick={() => navigate('/goals')}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  );
}

export default GoalsCreatePage;
